import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #navigation = 0;
  #renderedSearch = window.location.search;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    window.addEventListener('popstate', this.#handlePopState);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    window.removeEventListener('popstate', this.#handlePopState);
  }

  #handlePopState = async () => {
    if (window.location.search === this.#renderedSearch) return;
    const navigation = ++this.#navigation;
    const url = new URL(window.location.href);
    await sectionRenderer.renderSection(this.sectionId, { url });
    if (navigation === this.#navigation) this.#renderedSearch = url.search;
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  async onPaginationControlClick(data, event) {
    event.preventDefault();

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    const newURL = new URL(link?.href ?? window.location.href);
    if (!link) {
      for (const [key, value] of Object.entries(data)) {
        newURL.searchParams.set(key, value);
      }
    }
    newURL.hash = '';

    const navigation = ++this.#navigation;
    await sectionRenderer.renderSection(this.sectionId, { url: new URL(newURL) });
    if (navigation !== this.#navigation) return;

    history.pushState('', '', newURL.toString());
    this.#renderedSearch = newURL.search;

    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
